import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Send, 
  User, 
  Loader2, 
  Mic, 
  MicOff,
  X,
  Minimize2,
  Maximize2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { useConversationContext } from "@/hooks/useConversationContext";
import { RichMessage } from "./RichMessage";
import { ContextualActions } from "./ContextualActions";
import { VoiceWaveform } from "./VoiceWaveform";
import logo from "@/assets/logo.png";
import { cn } from "@/lib/utils";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  data?: Record<string, unknown>;
  timestamp: Date;
  isStreaming?: boolean;
}

interface MemoAIChatProps {
  companyId?: string;
  companyName?: string;
  isOpen?: boolean;
  onClose?: () => void;
  embedded?: boolean;
}

export function MemoAIChat({ 
  companyId, 
  companyName, 
  isOpen = true, 
  onClose,
  embedded = false 
}: MemoAIChatProps) {
  const navigate = useNavigate();
  const { context, setActiveCompany, addMessage: addContextMessage, updateLastIntent, getContextSummary } = useConversationContext();
  
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content: companyName 
        ? `Hey! I'm Memo, your assistant for **${companyName}**. Ask me anything about your KPIs, or tap the mic to talk.`
        : "Hey! I'm Memo. Select a company to get started, or ask me to list your companies.",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Set active company in conversation context
  useEffect(() => {
    if (companyId && companyName) {
      setActiveCompany(companyId, companyName);
    }
  }, [companyId, companyName, setActiveCompany]);

  const { isRecording, isProcessing, toggleRecording } = useVoiceRecorder({
    onTranscript: (text) => {
      setInput(text);
      setTimeout(() => sendMessage(text), 100);
    },
    onError: (error) => toast.error(error),
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleNavigate = useCallback((path: string) => {
    // Close the chat and navigate
    onClose?.();
    navigate(path);
  }, [navigate, onClose]);

  const sendMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      content: messageText.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    addContextMessage({ role: "user", content: messageText.trim() });

    const streamingId = `assistant-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: streamingId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
      isStreaming: true,
    }]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Please log in to use Memo AI");
      }

      const contextSummary = getContextSummary();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-router`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            input: messageText,
            company_id: companyId,
            channel: "web",
            conversation_context: contextSummary,
          }),
        }
      );

      if (!response.ok) {
        if (response.status === 429) throw new Error("Rate limit exceeded. Please wait a moment.");
        if (response.status === 402) throw new Error("AI credits exhausted.");
        throw new Error("Failed to get response");
      }

      const data = await response.json();

      // Update conversation context
      if (data.intent) updateLastIntent(data.intent);
      addContextMessage({ role: "assistant", content: data.response || "" });

      // Handle navigation action
      if (data.data?.action === "navigate" && data.data?.path) {
        setTimeout(() => handleNavigate(data.data.path as string), 1500);
      }

      setMessages(prev => prev.map(msg => 
        msg.id === streamingId 
          ? {
              ...msg,
              content: data.response || "I couldn't process that request.",
              data: data.data,
              isStreaming: false,
            }
          : msg
      ));
    } catch (error) {
      console.error("Memo AI error:", error);
      setMessages(prev => prev.map(msg => 
        msg.id === streamingId 
          ? {
              ...msg,
              content: error instanceof Error ? error.message : "Something went wrong. Please try again.",
              isStreaming: false,
            }
          : msg
      ));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [companyId, isLoading, addContextMessage, updateLastIntent, getContextSummary, handleNavigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  if (!isOpen) return null;

  const containerClass = embedded
    ? "flex flex-col h-full"
    : cn(
        "fixed z-50 bg-card border shadow-2xl transition-all duration-300 ease-out",
        isExpanded
          ? "inset-4 rounded-3xl"
          : "bottom-20 sm:bottom-6 right-4 sm:right-6 w-[calc(100%-2rem)] sm:w-[400px] h-[70vh] sm:h-[600px] max-h-[80vh] rounded-3xl"
      );

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-gradient-to-r from-primary/5 via-primary/10 to-accent/5 rounded-t-3xl">
        <Avatar className="h-10 w-10 border-2 border-primary/20 shadow-md overflow-hidden">
          <img src={logo} alt="Memo" className="h-full w-full object-cover" />
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            Memo AI
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-success/20 text-success border border-success/30">
              Online
            </span>
          </h3>
          <p className="text-xs text-muted-foreground truncate">
            {companyName ? `Managing ${companyName}` : "Your KPI assistant"}
          </p>
        </div>
        {!embedded && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-xl"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-xl"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "flex gap-3 animate-in-up",
                message.role === "user" ? "justify-end" : "justify-start"
              )}
            >
              {message.role === "assistant" && (
                <Avatar className="h-7 w-7 flex-shrink-0 shadow-sm mt-1 overflow-hidden">
                  <img src={logo} alt="Memo" className="h-full w-full object-cover" />
                </Avatar>
              )}
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-4 py-3 shadow-sm",
                  message.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-muted rounded-bl-md"
                )}
              >
                {message.isStreaming ? (
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="h-2 w-2 rounded-full bg-current animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="h-2 w-2 rounded-full bg-current animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="h-2 w-2 rounded-full bg-current animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                    <span className="text-sm">Thinking...</span>
                  </div>
                ) : message.role === "assistant" ? (
                  <RichMessage 
                    content={message.content} 
                    data={message.data}
                    animate={true}
                  />
                ) : (
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">
                    {message.content}
                  </div>
                )}
              </div>
              {message.role === "user" && (
                <Avatar className="h-7 w-7 flex-shrink-0 shadow-sm mt-1">
                  <AvatarFallback className="bg-secondary text-xs">
                    <User className="h-3.5 w-3.5" />
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Contextual Quick Actions */}
      <div className="px-4 py-2 border-t bg-muted/30">
        <ContextualActions
          companyId={companyId}
          companyName={companyName}
          onAction={sendMessage}
          disabled={isLoading}
        />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isRecording ? "Listening..." : "Ask Memo anything..."}
              disabled={isLoading || isRecording}
              className="pr-12 h-11 rounded-xl bg-muted/50 border-0 focus-visible:ring-1 text-sm"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className={cn(
                "absolute right-1 top-0.5 h-10 w-10 rounded-xl transition-all",
                isRecording && "bg-danger text-danger-foreground animate-pulse-glow"
              )}
              onClick={toggleRecording}
              disabled={isLoading || isProcessing}
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isRecording ? (
                <MicOff className="h-4 w-4" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </Button>
          </div>
          <Button 
            type="submit" 
            size="icon" 
            disabled={isLoading || !input.trim()}
            className="h-11 w-11 rounded-xl bg-gradient-accent shadow-md hover:shadow-lg transition-all"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
